import React, { useEffect, useMemo, useState } from 'react';
import {
  ArrowRight,
  Check,
  Gift,
  Loader2,
  Shirt,
  UserX,
} from 'lucide-react';
import { CleroButtonWordmark, CleroWordmarkSvg } from './Logo';
import CompareShell, { AppStoreBadges, InstitutionalTrustBar } from './compare/CompareShell';
import PhoneInput from './PhoneInput';
import { firebaseProjectId, functions, isUsingFirebaseEmulator } from '../services/firebase';
import { httpsCallable } from 'firebase/functions';
import { resolveMerchMeta } from '../services/merchCatalog';

type Props = {
  username: string;
};

type ProfileResolution =
  | { state: 'loading' }
  | { state: 'resolved'; displayName: string | null; firstName: string | null; photoURL: string | null }
  | { state: 'not_found' };

const getResolvePublicProfileUrl = (username: string) => {
  const encoded = encodeURIComponent(username);
  if (isUsingFirebaseEmulator) {
    return `http://${window.location.hostname}:5001/${firebaseProjectId}/us-central1/resolvePublicProfile?username=${encoded}`;
  }
  return `https://us-central1-${firebaseProjectId}.cloudfunctions.net/resolvePublicProfile?username=${encoded}`;
};

const UserProfileLandingPage: React.FC<Props> = ({ username }) => {
  const cleanUsername = useMemo(() => {
    return String(username || '')
      .trim()
      .toLowerCase()
      .replace(/^@+/, '')
      .replace(/[^a-z0-9_.]/g, '');
  }, [username]);

  const [resolution, setResolution] = useState<ProfileResolution>({ state: 'loading' });

  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      if (!cleanUsername) {
        setResolution({ state: 'not_found' });
        return;
      }
      try {
        const response = await fetch(getResolvePublicProfileUrl(cleanUsername), {
          method: 'GET',
          headers: { Accept: 'application/json' },
        });
        const data = await response.json();
        if (cancelled) return;
        if (data?.ok) {
          setResolution({
            state: 'resolved',
            displayName: data?.displayName ? String(data.displayName) : null,
            firstName: data?.firstName ? String(data.firstName) : null,
            photoURL: data?.photoURL ? String(data.photoURL) : null,
          });
          return;
        }
        setResolution({ state: 'not_found' });
      } catch {
        if (!cancelled) setResolution({ state: 'not_found' });
      }
    };
    run();
    return () => {
      cancelled = true;
    };
  }, [cleanUsername]);

  const deepLink = useMemo(() => `clero://u/${encodeURIComponent(cleanUsername)}`, [cleanUsername]);

  const openInClero = () => {
    window.location.href = deepLink;
  };

  const displayName = resolution.state === 'resolved' && resolution.displayName
    ? resolution.displayName
    : null;

  const firstName = resolution.state === 'resolved' && resolution.firstName
    ? resolution.firstName
    : (displayName ? displayName.split(/\s+/)[0] : null);

  const photoURL = resolution.state === 'resolved' && resolution.photoURL
    ? resolution.photoURL
    : null;

  const initials = useMemo(() => {
    if (displayName) {
      const parts = displayName.split(/\s+/).filter(Boolean);
      if (parts.length >= 2) return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
      if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
    }
    return cleanUsername ? cleanUsername.slice(0, 2).toUpperCase() : 'CL';
  }, [displayName, cleanUsername]);

  const recipientLabel = firstName || displayName || (cleanUsername ? `@${cleanUsername}` : 'your friend');

  const searchParams = useMemo(() => {
    if (typeof window === 'undefined') return new URLSearchParams();
    return new URLSearchParams(window.location.search);
  }, []);

  const referralSource = searchParams.get('ref');
  const merchCodeParam = searchParams.get('code');
  const merchTypeParam = searchParams.get('type');
  const isMerchScan = referralSource === 'merch' || Boolean(merchCodeParam);
  const isQrScan =
    isMerchScan ||
    referralSource === 'qr' ||
    searchParams.get('source') === 'qr' ||
    searchParams.has('qr');
  const scannedMerchMeta = useMemo(() => resolveMerchMeta(merchTypeParam), [merchTypeParam]);

  const [phoneInput, setPhoneInput] = useState('');
  const [submittingPhone, setSubmittingPhone] = useState(false);
  const [reservationSuccess, setReservationSuccess] = useState(false);
  const [confirmedPhone, setConfirmedPhone] = useState<string | null>(null);
  const [reservationError, setReservationError] = useState<string | null>(null);

  const formattedReservedPhone = useMemo(() => {
    const digits = phoneInput.replace(/\D/g, '').slice(-10);
    if (digits.length === 10) {
      return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
    }
    return phoneInput;
  }, [phoneInput]);

  const handleReserveBonus = async (e: React.FormEvent) => {
    e.preventDefault();
    setReservationError(null);
    const cleanPhone = phoneInput.replace(/[^\d+]/g, '');
    let normalized = cleanPhone;
    if (normalized.length === 10 && !normalized.startsWith('+')) {
      normalized = `+1${normalized}`;
    } else if (normalized.length === 11 && normalized.startsWith('1')) {
      normalized = `+${normalized}`;
    }
    if (!/^\+1\d{10}$/.test(normalized)) {
      setReservationError('Please enter a valid 10-digit US phone number.');
      return;
    }
    const digits = normalized.replace(/\D/g, '').slice(-10);
    const submittedFormattedPhone = `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;

    setSubmittingPhone(true);
    try {
      const reserveFn = httpsCallable<
        { phone: string; referrerUsername?: string; merchCode?: string },
        { ok: boolean; phone: string; rewardAmount: number }
      >(functions, 'reserveReferralBonus');

      const res = await reserveFn({
        phone: normalized,
        referrerUsername: cleanUsername,
        merchCode: merchCodeParam || undefined,
      });
      if (!res?.data?.ok) {
        setReservationError('Could not reserve bonus. Please try again.');
        return;
      }
      setConfirmedPhone(submittedFormattedPhone);
      setReservationSuccess(true);
    } catch (err: any) {
      const msg = err?.message || 'Could not reserve bonus. Please try again.';
      setReservationError(msg);
    } finally {
      setSubmittingPhone(false);
    }
  };

  return (
    <CompareShell>
      {/* HERO / PROFILE SECTION WITH ATMOSPHERIC AMBIENT MESH */}
      <section className="relative overflow-hidden pt-8 sm:pt-14 pb-14 sm:pb-20 px-4 sm:px-6">
        {/* Soft Ambient Radial Lights (Faithful to Landing Page public-hero-mesh) */}
        <div
          aria-hidden="true"
          className="pointer-events-none absolute -top-32 left-1/2 -translate-x-1/2 h-[500px] w-full max-w-5xl rounded-full bg-[radial-gradient(circle_at_50%_20%,rgba(13,148,136,0.12),rgba(98,199,255,0.06)_40%,transparent_70%)] blur-3xl -z-10"
        />
        <div
          aria-hidden="true"
          className="pointer-events-none absolute top-1/4 -left-32 h-72 w-72 rounded-full bg-teal-400/8 blur-3xl -z-10"
        />
        <div
          aria-hidden="true"
          className="pointer-events-none absolute top-1/3 -right-32 h-72 w-72 rounded-full bg-sky-400/8 blur-3xl -z-10"
        />

        {/* PROFILE CARD */}
        <div className="relative mx-auto max-w-xl rounded-[32px] sm:rounded-[40px] bg-white/95 backdrop-blur-2xl p-8 sm:p-12 shadow-[0_28px_80px_-16px_rgba(15,23,42,0.10),0_1px_2px_rgba(15,23,42,0.04)] border border-slate-200/80 ring-1 ring-white/60 text-center space-y-6">
          {resolution.state === 'loading' ? (
            /* ZERO-CLS SKELETON / SPINNER LOADING STATE */
            <div className="py-2 space-y-6 animate-pulse">
              {/* Avatar Skeleton */}
              <div className="relative mx-auto h-24 w-24 sm:h-28 sm:w-28 rounded-full bg-slate-100 ring-4 ring-slate-100/80 flex items-center justify-center shadow-inner">
                <Loader2 className="h-8 w-8 text-teal-600 animate-spin" />
              </div>

              {/* Title, Username & Subtitle Skeleton */}
              <div className="space-y-2.5 max-w-md mx-auto">
                <div className="h-9 bg-slate-100 rounded-full w-52 mx-auto" />
                <div className="h-4 bg-slate-100 rounded-full w-24 mx-auto" />
                <div className="h-3 bg-teal-100/60 rounded-full w-12 mx-auto pt-0.5" />
                <div className="h-4 bg-slate-100 rounded-full w-4/5 mx-auto pt-2" />
              </div>

              {/* Button Skeleton */}
              <div className="pt-2">
                <div className="h-12 bg-teal-600/20 rounded-full w-56 mx-auto" />
              </div>

              {/* Store Badges Skeleton */}
              <div className="space-y-2.5 pt-1">
                <div className="h-3 bg-slate-100 rounded-full w-56 mx-auto" />
                <div className="h-11 bg-slate-100 rounded-xl w-64 mx-auto" />
              </div>
            </div>
          ) : resolution.state === 'not_found' ? (
            /* PROFILE NOT FOUND CARD */
            <div className="space-y-6">
              {/* Subtle UserX Icon Badge */}
              <div className="flex justify-center">
                <div className="flex h-20 w-20 sm:h-24 sm:w-24 items-center justify-center rounded-full bg-slate-100 ring-4 ring-slate-100/90 text-slate-400 shadow-inner">
                  <UserX className="h-9 w-9 sm:h-11 sm:w-11" strokeWidth={1.8} />
                </div>
              </div>

              {/* Headline & Explanation */}
              <div className="space-y-1.5">
                <h1 className="text-3xl sm:text-4xl font-black tracking-tight text-slate-950 leading-tight">
                  Profile not found
                </h1>
                <p className="text-base sm:text-lg font-semibold text-slate-500">
                  @{cleanUsername}
                </p>
                <p className="mx-auto max-w-md text-sm sm:text-base text-slate-600 font-medium leading-relaxed pt-2">
                  We couldn&apos;t find an active Clero account for this username. The handle may be misspelled or hasn&apos;t been claimed yet.
                </p>
              </div>

              {/* Download App Notice & Store Badges */}
              <div className="space-y-3 pt-2">
                <p className="text-xs sm:text-sm font-semibold text-slate-500">
                  Download Clero to search for friends or claim your handle:
                </p>
                <div className="flex items-center justify-center">
                  <AppStoreBadges badgeHeightClass="h-10 sm:h-11" />
                </div>
              </div>

              {/* Back to Home Link */}
              <div className="pt-2">
                <a
                  href="/"
                  className="inline-flex items-center gap-1.5 text-xs sm:text-sm font-bold text-teal-700 hover:text-teal-800 transition-colors"
                >
                  <span>Go to Clero Home</span>
                  <ArrowRight className="h-3.5 w-3.5" strokeWidth={2.4} />
                </a>
              </div>
            </div>
          ) : (
            /* RESOLVED PROFILE CONTENT */
            <>
              {/* Clean Elevated Avatar without fake tick mark */}
              <div className="flex justify-center">
                {photoURL ? (
                  <div className="relative h-24 w-24 sm:h-28 sm:w-28 rounded-full p-1 ring-4 ring-slate-100/90 shadow-lg shadow-slate-900/5 transition-transform duration-300 hover:scale-105">
                    <img
                      src={photoURL}
                      alt={displayName || cleanUsername}
                      className="h-full w-full rounded-full object-cover"
                    />
                  </div>
                ) : (
                  <div className="flex h-24 w-24 sm:h-28 sm:w-28 items-center justify-center rounded-full bg-gradient-to-br from-teal-500 to-teal-700 text-white font-black text-2xl sm:text-3xl shadow-lg shadow-teal-950/10 border-2 border-white ring-4 ring-slate-100/90 transition-transform duration-300 hover:scale-105">
                    {initials}
                  </div>
                )}
              </div>

              {/* Heading, Clean Typography Handle (No pills), and App Clero Badge */}
              <div className="space-y-1.5">
                {displayName ? (
                  <>
                    <h1 className="text-3xl sm:text-4xl font-black tracking-tight text-slate-950 leading-tight">
                      {displayName}
                    </h1>
                    <p className="text-base sm:text-lg font-semibold text-slate-500">
                      @{cleanUsername}
                    </p>
                  </>
                ) : (
                  <h1 className="text-3xl sm:text-4xl font-black tracking-tight text-slate-950 leading-tight">
                    @{cleanUsername}
                  </h1>
                )}

                {/* Clero Wordmark Badge (Faithful to mobile app UserProfileScreen heroBadgeWrap) */}
                <div className="flex items-center justify-center pt-1" title="Clero Member">
                  <CleroWordmarkSvg width={48} height={15} color="#0D9488" className="inline-block" />
                </div>

                {/* Merch Showcase Card with Real Shirt Render */}
                {isMerchScan && (
                  <div className="mx-auto max-w-md rounded-2xl bg-[#F8FAFC] p-4 text-left flex items-center gap-3.5">
                    {/* Product Render Frame with Real Shirt Image */}
                    <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded-2xl bg-transparent overflow-hidden">
                      <img
                        src={scannedMerchMeta.imageUri}
                        alt={scannedMerchMeta.name}
                        className="h-full w-full object-contain"
                      />
                    </div>

                    {/* Product Details */}
                    <div className="min-w-0 flex-1">
                      <div className="text-sm font-bold text-slate-900 truncate">
                        {scannedMerchMeta.name}
                      </div>
                      <div className="text-xs font-medium text-slate-500">
                        {scannedMerchMeta.subtitle}
                      </div>
                    </div>
                  </div>
                )}

                {isMerchScan ? (
                  <p className="mx-auto max-w-md text-sm sm:text-base text-slate-600 font-medium leading-relaxed pt-1">
                    You scanned <span className="font-bold text-slate-900">{recipientLabel}&apos;s</span> Clero tee. Reserve your credit below so you both get rewarded on your first request.
                  </p>
                ) : (
                  <p className="mx-auto max-w-md text-sm sm:text-base text-slate-600 font-medium leading-relaxed pt-2">
                    Pay, request, or split expenses with <span className="font-bold text-slate-900">{recipientLabel}</span> directly from your bank account.
                  </p>
                )}

                {/* Phone Bonus Reservation Card or Celebration State */}
                {!reservationSuccess ? (
                  <div className="mx-auto max-w-md rounded-2xl bg-[#F8FAFC] p-5 text-left space-y-3.5">
                    <div className="flex items-center gap-3">
                      <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-emerald-600 text-white shadow-xs">
                        <Gift className="h-5 w-5" />
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="text-[11px] font-bold uppercase tracking-wider text-emerald-800">
                          Referral Credit
                        </div>
                        <div className="text-sm sm:text-base font-bold text-slate-900">
                          Get $5 on your first request
                        </div>
                      </div>
                    </div>

                    <p className="text-xs sm:text-sm text-slate-600 font-medium leading-relaxed">
                      Enter your phone number to reserve your credit. When you sign up with this number and complete your first request, both you and <span className="font-semibold text-slate-800">{recipientLabel}</span> get rewarded.
                    </p>

                    <form onSubmit={handleReserveBonus} className="space-y-3 pt-1">
                      <PhoneInput
                        value={phoneInput}
                        onChange={(val) => {
                          setPhoneInput(val);
                          if (reservationError) setReservationError(null);
                        }}
                        placeholder="(555) 000-0000"
                        label=""
                      />

                      {reservationError && (
                        <p className="text-xs font-semibold text-red-600">
                          {reservationError}
                        </p>
                      )}

                      <button
                        type="submit"
                        disabled={submittingPhone || phoneInput.replace(/\D/g, '').length < 10}
                        className="w-full inline-flex items-center justify-center gap-2 rounded-full bg-[#0D9488] hover:bg-[#0B7C71] active:bg-[#09665D] disabled:opacity-50 disabled:cursor-not-allowed text-white font-bold text-base py-3.5 px-6 transition-colors shadow-none cursor-pointer border-0"
                      >
                        {submittingPhone ? (
                          <>
                            <Loader2 className="h-4 w-4 animate-spin" />
                            <span>Reserving credit...</span>
                          </>
                        ) : (
                          <span>Claim Referral Credit</span>
                        )}
                      </button>
                    </form>
                  </div>
                ) : (
                  <div className="mx-auto max-w-md rounded-2xl bg-[#F8FAFC] p-6 text-center space-y-4">
                    <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-emerald-600 text-white shadow-xs">
                      <Check className="h-7 w-7 stroke-[2.5]" />
                    </div>

                    <div className="space-y-1">
                      <h3 className="text-lg sm:text-xl font-black text-slate-900">
                        Credit Reserved
                      </h3>
                      <p className="text-sm font-semibold text-emerald-700">
                        Held for {confirmedPhone || formattedReservedPhone}
                      </p>
                    </div>

                    <p className="text-xs sm:text-sm text-slate-600 font-medium leading-relaxed">
                      Download Clero below and sign up with this phone number. Once you complete your first request, both you and <span className="font-semibold text-slate-800">{recipientLabel}</span> will receive your credit.
                    </p>

                    <div className="pt-2 flex items-center justify-center">
                      <AppStoreBadges badgeHeightClass="h-11 sm:h-12" />
                    </div>
                  </div>
                )}
              </div>

              {/* Open in Clero action for existing app users (hidden on QR scans since it does not apply) */}
              {!isQrScan && (
                <div className="pt-2">
                  <button
                    type="button"
                    onClick={openInClero}
                    className="inline-flex items-center justify-center rounded-full bg-[#0D9488] hover:bg-[#0B7C71] active:bg-[#09665D] text-white font-bold text-base px-8 py-3.5 transition-colors shadow-none border-0 w-full sm:w-auto cursor-pointer"
                  >
                    <span className="whitespace-nowrap inline-flex items-center gap-1.5 leading-none">
                      <span>Open in</span>
                      <CleroButtonWordmark height={15} color="#FFFFFF" className="inline-block shrink-0" />
                    </span>
                  </button>
                </div>
              )}

              {!reservationSuccess && (
                <div className="space-y-3 pt-1">
                  <p className="text-xs sm:text-sm font-semibold text-slate-500">
                    Download Clero free for iOS and Android
                  </p>
                  <div className="flex items-center justify-center">
                    <AppStoreBadges badgeHeightClass="h-10 sm:h-11" />
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </section>

      {/* INSTITUTIONAL BANKING INFRASTRUCTURE & SECURITY METRICS BAR (Matching Landing Page) */}
      <InstitutionalTrustBar />
    </CompareShell>
  );
};

export default UserProfileLandingPage;

